import {useQuery} from '@tanstack/react-query';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {ApiError, ProductRoadmapService, type Timeframe} from 'client';
import {useAtomValue} from 'jotai';
import {getService} from 'utilities';
import {useTimeframesQuery} from 'views/workstreams/detail/hooks';
import {
  getDefaultWorkstreamTimeframe,
  getTimeframeByName,
} from 'views/workstreams/detail/workstreamTimeframe';

export const getProductRoadmapTimeframe = (
  requestedTimeframe: string | null | undefined,
  timeframes: readonly Timeframe[]
) =>
  getTimeframeByName(
    requestedTimeframe
      ? requestedTimeframe.trim().replace(/[-_]+/g, ' ').toUpperCase()
      : getDefaultWorkstreamTimeframe(new Date(), timeframes),
    timeframes
  );

type ProductRoadmapScope = {timeframeId?: number; workstreamId?: number};

export const productRoadmapQueryKey = ({timeframeId, workstreamId}: ProductRoadmapScope) =>
  [
    'product-roadmap',
    'embed-config',
    {timeframe_id: timeframeId ?? null, workstream_id: workstreamId ?? null},
  ] as const;

export const useProductRoadmapDocument = ({
  timeframeId,
  workstreamId,
  enabled = true,
}: ProductRoadmapScope & {enabled?: boolean}) =>
  useQuery({
    queryKey: productRoadmapQueryKey({timeframeId, workstreamId}),
    enabled: enabled && Boolean(timeframeId || workstreamId),
    queryFn: async () => {
      try {
        return await getService(
          ProductRoadmapService
        ).getProductRoadmapEmbedConfigApiV1ProductRoadmapEmbedConfigGet(timeframeId, workstreamId);
      } catch (error) {
        if (
          workstreamId !== undefined &&
          error instanceof ApiError &&
          error.status === 404 &&
          error.body?.detail === 'Product Roadmap document is not configured for workstream'
        ) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
  });

export const useHasTimeframeProductRoadmap = (requestedTimeframe?: string | null) => {
  const {isProductRoadmapEnabled} = useAtomValue(growthbookCacheAtom);
  const {timeframes} = useTimeframesQuery({enabled: isProductRoadmapEnabled});
  const timeframe = getProductRoadmapTimeframe(requestedTimeframe, timeframes);
  const {data, isError} = useProductRoadmapDocument({
    timeframeId: timeframe?.id,
    enabled: isProductRoadmapEnabled,
  });
  return isProductRoadmapEnabled && Boolean(data) && !isError;
};
